import { GraphChildProps, rangeChanged, TimeRange, TimeRangeTupleTs } from '@mi-front/core';
import { styled } from '@mui/material/styles';
import { palette } from '@ui/styles';
import { BrushBehavior, brushX, D3BrushEvent } from 'd3-brush';
import * as d3Sel from 'd3-selection';
import throttle from 'lodash/throttle';
import React from 'react';

const StyledTimeBrush = styled('g')(({ theme }) => ({
  '& .selection': {
    fill: `${palette.green}`, // transparentize($color-Green, 0.25)
    fillOpacity: 0.35,
    shapeRendering: 'crispEdges',
  },
  '& .selection__handle': {
    fill: palette.green, // $color-Green
    cursor: 'ew-resize',
  },
  '& .handle': {
    fill: palette.green, // $color-Green
  },
}));

interface Props extends GraphChildProps {
  readonly selectionRange: TimeRange<number>;
  readonly minSelectionLength?: number; // ms
  readonly onStart: (range: TimeRangeTupleTs, isResizing: boolean) => void;
  readonly onChange: (range: TimeRangeTupleTs, isResizing: boolean) => void;
  readonly onEnd: (range: TimeRangeTupleTs, isResizing: boolean) => void;
  readonly updateThrottleMs: number;
}

interface State {
  readonly isResizing: boolean;
}

type BrushSelection = [number, number];

export class TimeBrush extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    minSelectionLength: 2 * 60 * 1000,
    updateThrottleMs: 50,
  };

  state: State = {
    isResizing: false,
  };

  private focusBrush: BrushBehavior<number> = null!;
  private brushEl = React.createRef<SVGGElement>();
  private brushHandles: d3Sel.Selection<SVGGElement, number, any, any> = null!;
  private isUpdatingProgramatically = false;

  /**
   * Checks if dimensions (width/height) have changed between props.
   */
  private hasDimensionsChanged(prevProps: Props, nextProps: Props): boolean {
    return (
      prevProps.parentWidth !== nextProps.parentWidth ||
      prevProps.parentHeight !== nextProps.parentHeight
    );
  }

  shouldComponentUpdate(nextProps: Props) {
    const rangeChanged_ = rangeChanged(this.props.selectionRange, nextProps.selectionRange);
    const dimensionsChanged = this.hasDimensionsChanged(this.props, nextProps);
    return rangeChanged_ || dimensionsChanged;
  }

  componentDidMount() {
    this.initializeBrush();
    this.adjustInitialBrush();
  }

  componentDidUpdate(prevProps: Props) {
    // If dimensions changed, re-initialize the brush
    if (this.hasDimensionsChanged(prevProps, this.props)) {
      this.initializeBrush();
    }

    const selection = this.timeRangeToSelection(this.props.selectionRange);
    if (!selection) {
      return;
    }

    const brush = d3Sel.select<SVGGElement, number>(this.brushEl.current!);
    this.focusBrush.move(brush, selection);
    this.adjustBrushHandles(selection);
  }

  /**
   * Initializes the D3 brush and sets up event handlers.
   * This method combines brush creation and event setup since they must always be called together.
   */
  private initializeBrush() {
    this.setupFocusBrush();
    this.setupEvents();
  }

  /**
   * Initializes the D3 brush required to select the current focus.
   */
  private setupFocusBrush() {
    const { parentWidth, parentHeight } = this.props;

    this.focusBrush = brushX<number>()
      .handleSize(1)
      .extent([
        [0, 0],
        [parentWidth, parentHeight],
      ]);

    d3Sel.select<SVGGElement, number>(this.brushEl.current!).call(this.focusBrush);

    this.brushHandles = d3Sel.select(this.brushEl.current!).selectAll('.selection__handle');

    this.brushHandles
      .data([{ type: 'w' }, { type: 'e' }])
      .enter()
      .append('circle')
      .attr('class', 'selection__handle')
      .attr('cursor', 'ew-resize')
      .attr('display', 'none')
      .attr('cy', parentHeight / 2)
      .attr('r', 8);
  }

  adjustInitialBrush() {
    const selection = this.timeRangeToSelection(this.props.selectionRange);
    if (!selection) {
      return;
    }

    this.moveBrush(selection);
    this.adjustBrushHandles(selection);
  }

  /**
   * Converts a time range to pixel coordinates selection.
   */
  private timeRangeToSelection(range: TimeRange<number>): BrushSelection | null {
    const { scale } = this.props;
    if (!range.from || !range.to) {
      return null;
    }
    return [scale(range.from)!, scale(range.to)!];
  }

  moveBrush(selection: [number, number]) {
    this.isUpdatingProgramatically = true;

    this.focusBrush.move(d3Sel.select<SVGGElement, number>(this.brushEl.current!), selection);

    this.isUpdatingProgramatically = false;
  }

  private setupEvents() {
    const { onStart, onChange, onEnd, updateThrottleMs } = this.props;

    const ignoreProgramaticChange = (fn: () => any) => () => {
      if (!d3Sel.event.sourceEvent || this.isUpdatingProgramatically) {
        return;
      }
      fn();
    };

    const throttledOnChange = throttle((range: TimeRangeTupleTs, isResizing: boolean) => {
      onChange(range, isResizing);
    }, updateThrottleMs);

    const handleBrushEvent = (callback: (range: TimeRangeTupleTs, isResizing: boolean) => void) => {
      const { selection } = d3Sel.event;
      const validSelection = this.enforceMinimumBrushSize(selection);
      if (!validSelection) {
        return;
      }

      callback(this.format(validSelection), this.state.isResizing);
      this.adjustBrushHandles(validSelection);
    };

    this.focusBrush
      .on(
        'start',
        ignoreProgramaticChange(() => {
          const { selection, sourceEvent } = d3Sel.event as D3BrushEvent<number>;

          const validSelection = this.enforceMinimumBrushSize(selection as BrushSelection);
          if (!validSelection) {
            return;
          }

          const isResizing = this.detectIsResizing(sourceEvent);
          this.setState({ isResizing });
          onStart(this.format(validSelection), isResizing);
          this.adjustBrushHandles(validSelection);
        }),
      )
      .on(
        'brush',
        ignoreProgramaticChange(() => handleBrushEvent(throttledOnChange)),
      )
      .on(
        'end',
        ignoreProgramaticChange(() => handleBrushEvent(onEnd)),
      );
  }

  /**
   * Detects if the user is resizing an existing selection based on the event target.
   * For SVG elements className is SVGAnimatedString (use baseVal); for HTML it is a string.
   */
  private detectIsResizing(sourceEvent: Event): boolean {
    const target = sourceEvent?.target as Element | null;

    if (!target?.className) return false;

    const classStr =
      typeof target.className === 'string'
        ? target.className
        : ((target.className as SVGAnimatedString).baseVal ?? '');

    return !!classStr.match(/handle|overlay/);
  }

  format(selection: BrushSelection) {
    const { scale } = this.props;

    return [Number(scale.invert(selection[0])), Number(scale.invert(selection[1]))] as const;
  }

  /**
   * Validates if the selection meets the minimum length requirement.
   * @returns true if selection is valid, false otherwise
   */
  private validateMinimumBrushSize(selection: BrushSelection): boolean {
    const { scale, minSelectionLength } = this.props;

    if (!selection || selection[0] === selection[1]) {
      return false;
    }

    const selectedLength =
      scale.invert(selection[1]).valueOf() - scale.invert(selection[0]).valueOf();
    return selectedLength >= (minSelectionLength ?? 0);
  }

  /**
   * Enforces minimum brush size by correcting the selection if needed.
   * @returns the selection to use (original if valid, fixed if corrected, null if can't fix)
   */
  private enforceMinimumBrushSize(selection: BrushSelection): BrushSelection | null {
    if (this.validateMinimumBrushSize(selection)) {
      return selection;
    }

    if (!selection) {
      return null;
    }

    const fixedSelection =
      selection[0] === selection[1]
        ? this.createCenteredSelection(selection[0])
        : this.extendSelectionToMinimum(selection);

    if (fixedSelection) {
      this.moveBrush(fixedSelection);
    }
    return fixedSelection;
  }

  /**
   * Creates a centered selection around a click point with minimum length.
   */
  private createCenteredSelection(clickPoint: number): BrushSelection | null {
    const { scale, parentWidth, minSelectionLength } = this.props;
    const minLength = minSelectionLength ?? 0;
    const clickTime = Number(scale.invert(clickPoint));
    const halfLength = minLength / 2;

    let fixedFrom = scale(clickTime - halfLength)!;
    let fixedTo = scale(clickTime + halfLength)!;

    // Adjust if selection exceeds bounds
    if (fixedFrom < 0) {
      fixedFrom = 0;
      fixedTo = scale(clickTime + minLength)!;
    } else if (fixedTo > parentWidth) {
      fixedFrom = scale(clickTime - minLength)!;
      fixedTo = parentWidth;
    }

    return [fixedFrom, fixedTo];
  }

  /**
   * Extends a selection to meet the minimum length requirement.
   */
  private extendSelectionToMinimum(selection: BrushSelection): BrushSelection | null {
    const { scale, parentWidth, minSelectionLength } = this.props;
    const minLength = minSelectionLength ?? 0;

    let fixedFrom = selection[0];
    let fixedTo = scale(Number(scale.invert(selection[0])) + minLength)!;

    // If fixed selection exceeds parent width, align from the right
    if (fixedTo > parentWidth) {
      fixedFrom = scale(Number(scale.invert(selection[1])) - minLength)!;
      fixedTo = selection[1];
    }

    return [fixedFrom, fixedTo];
  }

  adjustBrushHandles(selection: BrushSelection) {
    d3Sel
      .selectAll('.selection__handle')
      .attr('cx', (_d, i) => selection[i])
      .attr('display', null);
  }

  render() {
    return <StyledTimeBrush ref={this.brushEl} />;
  }
}
